import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Custom hook to block navigation when there's unsaved work
 * @param shouldBlock - Whether navigation should be blocked
 * @param message - Optional custom confirmation message
 */
export function useNavigationBlocker(
  shouldBlock: boolean,
  message = 'You have unsaved changes. Are you sure you want to leave?',
) {
  const navigator = useContext(NavigationContext).navigator;

  useEffect(() => {
    if (!shouldBlock) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    const confirmNavigation = () => {
      return window.confirm(message);
    };

    // Override push method
    navigator.push = (...args: Parameters<typeof navigator.push>) => {
      if (confirmNavigation()) {
        originalPush.apply(navigator, args);
      }
    };

    // Override replace method
    navigator.replace = (...args: Parameters<typeof navigator.replace>) => {
      if (confirmNavigation()) {
        originalReplace.apply(navigator, args);
      }
    };

    // Block browser back/forward navigation
    const handlePopState = () => {
      if (!confirmNavigation()) {
        // User cancelled, push current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Add a dummy state to enable popstate blocking
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    // Warn before closing tab/window or refreshing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, message, navigator]);
}
